/**
 * The Wall-E list: the tokens this service decides itself (DEC-28).
 *
 * Decided 24.10.2026 by Lucian with Sebastian. A token on the list is answered
 * before anyone is asked: accepted is approved, blocked is refused. Everything
 * else goes to DCS, TIM or Hubject as before. An approval names no payer, so
 * the session goes where it would have gone without it.
 *
 * Admins change the list here; the OCPP Admin changes it through the API with
 * its own key. Everyone signed in reads it, tokens included, as they read the
 * decisions.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { EditRegular } from '@fluentui/react-icons'
import {
  Chip,
  DataTableShell,
  EmptyState,
  FilterBar,
  KeyValueList,
  KeyValueRow,
  KpiCard,
  LoadingState,
  MonoText,
  SectionCard,
  StatusBadge,
  formatTimestamp,
} from '../platform'
import { ApiError, WALL_E_LIST_KINDS, api } from '../api'
import type { WallEListEntry, WallEListImport, WallEListKind, WallEListPage } from '../api'
import { useAuth, problemText } from '../auth'

function said(e: unknown): string {
  return e instanceof ApiError ? problemText(e) : String(e)
}
import { KIND_LABELS, matches, totals, viaLabel, whoPays } from './wallEListReading'
import { WallEListSwitch } from './WallEListSwitch'

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  intro: { color: tokens.colorNeutralForeground2, maxWidth: '820px', margin: 0 },
  head: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalL,
  },
  actions: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
  kpis: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  columns: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))',
    gap: tokens.spacingHorizontalL,
    alignItems: 'start',
  },
  wide: { gridColumn: '1 / -1' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  table: { width: '100%', minWidth: '860px' },
  scroll: { overflowX: 'auto' },
  muted: { color: tokens.colorNeutralForeground3 },
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  points: { margin: 0, paddingLeft: tokens.spacingHorizontalXL, display: 'grid', gap: tokens.spacingVerticalS },
})

type Editing = { entry: WallEListEntry | null } | null

export default function WallEList() {
  const styles = useStyles()
  const { principal } = useAuth()
  const isAdmin = principal?.role === 'admin'
  const [entries, setEntries] = useState<WallEListEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState<WallEListKind | 'all'>('all')
  const [editing, setEditing] = useState<Editing>(null)
  const [importing, setImporting] = useState(false)

  const load = useCallback(async () => {
    try {
      // The whole list, page by page. A few hundred entries, read once, and
      // searched here so no token ever travels in a URL.
      const all: WallEListEntry[] = []
      let cursor: string | null = null
      do {
        const suffix: string = cursor ? `?limit=1000&cursor=${encodeURIComponent(cursor)}` : '?limit=1000'
        const page: WallEListPage = await api.get<WallEListPage>(`/wall-e-list${suffix}`)
        all.push(...page.entries)
        cursor = page.next_cursor
      } while (cursor)
      setEntries(all)
      setError(null)
    } catch (e) {
      setError(said(e))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const counted = useMemo(() => (entries ? totals(entries, Date.now()) : null), [entries])
  const shown = useMemo(
    () => (entries ?? []).filter((e) => (kind === 'all' || e.kind === kind) && matches(e, query)),
    [entries, kind, query],
  )

  if (error) return <EmptyState title="The Wall-E list cannot be read" description={error} />
  if (!entries || !counted) return <LoadingState label="Reading the Wall-E list" />

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <p className={styles.intro}>
          Tokens this service approves or refuses itself, without asking DCS, TIM or Hubject. The
          OCPP Admin reads and changes it through the API, and keeps a copy only for when this
          service cannot be reached.
        </p>
        {isAdmin ? (
          <div className={styles.actions}>
            <Button onClick={() => setImporting(true)}>Import CSV</Button>
            <Button appearance="primary" onClick={() => setEditing({ entry: null })}>
              Add token
            </Button>
          </div>
        ) : null}
      </div>

      <WallEListSwitch isAdmin={isAdmin} accepted={counted.accepted} blocked={counted.blocked} />

      <section aria-label="Totals" className={styles.kpis}>
        <KpiCard
          title="On the list"
          value={counted.total}
          tone="brand"
          caption={`${counted.accepted} accepted, ${counted.blocked} blocked`}
        />
        <KpiCard title="Cards" value={counted.byKind.card} caption="individual RFID cards" />
        <KpiCard
          title="Labels and placeholders"
          value={counted.byKind.label + counted.byKind.placeholder}
          caption="routing labels, ZERO, FREE and the like"
        />
        <KpiCard
          title="Kiosks and vehicles"
          value={counted.byKind.kiosk + counted.byKind.vehicle}
          caption={`${counted.byKind.kiosk} kiosk tags, ${counted.byKind.vehicle} vehicle ids`}
        />
        <KpiCard
          title="Used in 30 days"
          value={counted.usedIn30Days}
          caption={`${counted.neverUsed} never used`}
        />
      </section>

      <div className={styles.columns}>
        <div className={styles.wide}>
          <DataTableShell
            title={`${shown.length} of ${counted.total}`}
            controls={
              <FilterBar
                search={
                  <Input
                    type="search"
                    aria-label="Find a token"
                    placeholder="Find a token, any case"
                    value={query}
                    onChange={(_, d) => setQuery(d.value)}
                  />
                }
                filters={
                  <div className={styles.chips} role="group" aria-label="Kind">
                    <Chip selected={kind === 'all'} onClick={() => setKind('all')}>
                      All {counted.total}
                    </Chip>
                    {WALL_E_LIST_KINDS.filter((k) => counted.byKind[k] > 0).map((k) => (
                      <Chip key={k} selected={kind === k} onClick={() => setKind(k)}>
                        {KIND_LABELS[k]} {counted.byKind[k]}
                      </Chip>
                    ))}
                  </div>
                }
              />
            }
          >
            {shown.length === 0 ? (
              <EmptyState
                title={counted.total === 0 ? 'The list is empty' : 'No token matches'}
                description={
                  counted.total === 0
                    ? 'Nothing is on the Wall-E list yet. Import the OCPP Admin export, or add a token.'
                    : 'Nothing on the list matches this search and kind.'
                }
              />
            ) : (
              <div className={styles.scroll}>
                <Table aria-label="Entries" className={styles.table} size="small">
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>Token</TableHeaderCell>
                      <TableHeaderCell>Kind</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Who pays</TableHeaderCell>
                      <TableHeaderCell>Changed by</TableHeaderCell>
                      <TableHeaderCell>Last used</TableHeaderCell>
                      {isAdmin ? <TableHeaderCell aria-label="Edit" /> : null}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shown.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          {entry.token === null ? (
                            <span className={styles.muted}>could not be decrypted</span>
                          ) : (
                            <MonoText>{entry.token}</MonoText>
                          )}
                        </TableCell>
                        <TableCell>{KIND_LABELS[entry.kind] ?? entry.kind}</TableCell>
                        <TableCell>
                          <StatusBadge
                            status={entry.status === 'accepted' ? 'success' : 'error'}
                            label={entry.status === 'accepted' ? 'Accepted' : 'Blocked'}
                          />
                        </TableCell>
                        <TableCell>{whoPays(entry)}</TableCell>
                        <TableCell>
                          <span className={styles.stack}>
                            <span>{entry.updated_by}</span>
                            <span className={styles.muted}>{viaLabel(entry.updated_via)}</span>
                          </span>
                        </TableCell>
                        <TableCell>
                          {entry.last_used_at ? formatTimestamp(entry.last_used_at) : 'never'}
                        </TableCell>
                        {isAdmin ? (
                          <TableCell>
                            <Button
                              aria-label={`Edit ${entry.token ?? entry.id}`}
                              icon={<EditRegular />}
                              size="small"
                              onClick={() => setEditing({ entry })}
                            />
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </DataTableShell>
        </div>

        <SectionCard title="How this service uses it">
          <ol className={styles.points}>
            <li>A token on the list is answered at once, in any letter case, and the decision says Wall-E list.</li>
            <li>Accepted is approved and blocked is refused, before anyone is asked.</li>
            <li>Everything else goes to DCS, TIM or Hubject as before.</li>
            <li>
              An approval names no payer. The session goes to whoever approved a card at that station
              just before, or is kept here, as it was before the list moved here.
            </li>
            <li>It decides only while it is switched on, at the top of this page.</li>
          </ol>
        </SectionCard>

        <SectionCard
          title="API for the OCPP Admin"
          description="Its own key in X-API-Key, with a rotation slot (Settings). X-Actor names the person who made a change. The OCPP Admin asks for what changed every minute, for its offline copy."
        >
          <KeyValueList divided>
            <KeyValueRow label="Read" value={<MonoText>GET /api/v1/wall-e-list?changed_since=</MonoText>} />
            <KeyValueRow label="Add" value={<MonoText>POST /api/v1/wall-e-list</MonoText>} />
            <KeyValueRow label="Change" value={<MonoText>PATCH /api/v1/wall-e-list/{'{id}'}</MonoText>} />
            <KeyValueRow label="Remove" value={<MonoText>DELETE /api/v1/wall-e-list/{'{id}'}</MonoText>} />
          </KeyValueList>
        </SectionCard>
      </div>

      {editing ? (
        <EntryDialog
          entry={editing.entry}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await load()
          }}
        />
      ) : null}
      {importing ? (
        <ImportDialog
          onClose={() => setImporting(false)}
          onImported={async () => {
            await load()
          }}
        />
      ) : null}
    </div>
  )
}

function EntryDialog({
  entry,
  onClose,
  onSaved,
}: {
  entry: WallEListEntry | null
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const styles = useStyles()
  const [token, setToken] = useState('')
  const [kind, setKind] = useState<WallEListKind>(entry?.kind ?? 'other')
  const [status, setStatus] = useState<'accepted' | 'blocked'>(entry?.status ?? 'accepted')
  const [note, setNote] = useState(entry?.note ?? '')
  const [refused, setRefused] = useState<{ field: string | null; line: string } | null>(null)
  const [removing, setRemoving] = useState(false)

  const refuse = (e: unknown) => {
    if (e instanceof ApiError && e.problem.status === 409) {
      setRefused({ field: 'token', line: 'This token is already on the list. Change that entry instead.' })
      return
    }
    setRefused({
      field: e instanceof ApiError ? (e.problem.field ?? null) : null,
      line: said(e),
    })
  }

  const save = () => {
    setRefused(null)
    const fields = { kind, status, note: note.trim() ? note : null }
    const saving = entry
      ? api.patch(`/wall-e-list/${entry.id}`, fields)
      : api.post('/wall-e-list', { token, ...fields })
    saving.then(onSaved).catch(refuse)
  }

  const remove = () => {
    if (!entry) return
    api.delete(`/wall-e-list/${entry.id}`).then(onSaved).catch(refuse)
  }

  const fieldError = (name: string) => (refused?.field === name ? refused.line : undefined)

  return (
    <Dialog open onOpenChange={(_, d) => (d.open ? null : onClose())}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{entry ? 'Change an entry' : 'Add a token'}</DialogTitle>
          <DialogContent className={styles.form}>
            {entry ? (
              <Field label="Token" hint="A token never changes. To change it, remove this entry and add the new one.">
                <MonoText>{entry.token ?? 'could not be decrypted'}</MonoText>
              </Field>
            ) : (
              <Field
                label="Token"
                hint="As the station sends it. Letter case does not matter."
                validationMessage={fieldError('token')}
              >
                <Input value={token} onChange={(_, d) => setToken(d.value)} />
              </Field>
            )}
            <Field label="Kind" validationMessage={fieldError('kind')}>
              <Select value={kind} onChange={(_, d) => setKind(d.value as WallEListKind)}>
                {WALL_E_LIST_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABELS[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Status" validationMessage={fieldError('status')}>
              <RadioGroup
                value={status}
                onChange={(_, d) => setStatus(d.value as 'accepted' | 'blocked')}
                layout="horizontal"
              >
                <Radio value="accepted" label="Accepted, approved at once" />
                <Radio value="blocked" label="Blocked, refused at once" />
              </RadioGroup>
            </Field>
            <Field label="Note" hint="Optional. Never somebody else's card number." validationMessage={fieldError('note')}>
              <Textarea value={note} onChange={(_, d) => setNote(d.value)} resize="vertical" />
            </Field>
            {refused && !refused.field ? (
              <MessageBar intent="error">
                <MessageBarBody>{refused.line}</MessageBarBody>
              </MessageBar>
            ) : null}
            {removing ? (
              <MessageBar intent="warning">
                <MessageBarBody>
                  Remove this entry? The token is then decided by DCS, TIM or Hubject again. The OCPP
                  Admin learns of the removal on its next sync.
                </MessageBarBody>
              </MessageBar>
            ) : null}
          </DialogContent>
          <DialogActions>
            {entry && !removing ? <Button onClick={() => setRemoving(true)}>Remove</Button> : null}
            {removing ? (
              <>
                <Button onClick={() => setRemoving(false)}>Keep it</Button>
                <Button appearance="primary" onClick={remove}>
                  Remove the entry
                </Button>
              </>
            ) : (
              <>
                <Button onClick={onClose}>Cancel</Button>
                <Button appearance="primary" onClick={save} disabled={!entry && !token.trim()}>
                  {entry ? 'Save' : 'Add'}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

const VERDICT_WORDS: Record<string, string> = {
  new: 'new, will be added',
  unchanged: 'already on the list',
  conflicting: 'conflicting, a person decides',
  repeated: 'repeated in the file',
  invalid: 'cannot be read',
}

function ImportDialog({
  onClose,
  onImported,
}: {
  onClose: () => void
  onImported: () => Promise<void>
}) {
  const styles = useStyles()
  const [csv, setCsv] = useState('')
  const [reading, setReading] = useState<WallEListImport | null>(null)
  const [refused, setRefused] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const send = (dryRun: boolean) => {
    setRefused(null)
    setBusy(true)
    api
      .post<WallEListImport>('/wall-e-list/import', { csv, dry_run: dryRun })
      .then(async (answer) => {
        setReading(answer)
        if (!dryRun) await onImported()
      })
      .catch((e) => setRefused(said(e)))
      .finally(() => setBusy(false))
  }

  const pick = (file: File | undefined) => {
    if (!file) return
    setReading(null)
    void file.text().then(setCsv)
  }

  const notable = (reading?.rows ?? []).filter((r) => r.verdict !== 'unchanged')

  return (
    <Dialog open onOpenChange={(_, d) => (d.open ? null : onClose())}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Import the OCPP Admin export</DialogTitle>
          <DialogContent className={styles.form}>
            <p className={styles.muted}>
              A header row uuid;status, then one token per row, accepted or blocked. Check first: nothing is written until
              you import, and only new tokens are added. A conflict stays as it is until somebody changes the entry.
            </p>
            <Field label="CSV file">
              <input type="file" accept=".csv,text/csv" onChange={(e) => pick(e.target.files?.[0])} />
            </Field>
            <Field label="Or paste it">
              <Textarea
                value={csv}
                onChange={(_, d) => {
                  setCsv(d.value)
                  setReading(null)
                }}
                resize="vertical"
                rows={6}
              />
            </Field>
            {refused ? (
              <MessageBar intent="error">
                <MessageBarBody>{refused}</MessageBarBody>
              </MessageBar>
            ) : null}
            {reading ? (
              <>
                <MessageBar intent={reading.dry_run ? 'info' : 'success'}>
                  <MessageBarBody>
                    {reading.dry_run
                      ? `${reading.counts.new} new, ${reading.counts.unchanged} already on the list, ${reading.counts.conflicting} conflicting, ${reading.counts.repeated} repeated, ${reading.counts.invalid} cannot be read. Nothing is written yet.`
                      : `${reading.written} added.`}
                  </MessageBarBody>
                </MessageBar>
                {notable.length > 0 ? (
                  <div className={styles.scroll}>
                    <Table aria-label="What the file holds" size="extra-small">
                      <TableHeader>
                        <TableRow>
                          <TableHeaderCell>Line</TableHeaderCell>
                          <TableHeaderCell>Token</TableHeaderCell>
                          <TableHeaderCell>Status</TableHeaderCell>
                          <TableHeaderCell>Kind</TableHeaderCell>
                          <TableHeaderCell>Verdict</TableHeaderCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {notable.map((row) => (
                          <TableRow key={row.line}>
                            <TableCell>{row.line}</TableCell>
                            <TableCell>
                              <MonoText>{row.token ?? ''}</MonoText>
                            </TableCell>
                            <TableCell>{row.status ?? ''}</TableCell>
                            <TableCell>{row.kind ? KIND_LABELS[row.kind] : ''}</TableCell>
                            <TableCell>
                              {VERDICT_WORDS[row.verdict] ?? row.verdict}
                              {row.why ? `: ${row.why}` : ''}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : null}
              </>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
            <Button onClick={() => send(true)} disabled={!csv.trim() || busy}>
              Check
            </Button>
            <Button
              appearance="primary"
              onClick={() => send(false)}
              disabled={!reading || !reading.dry_run || reading.counts.new === 0 || busy}
            >
              {reading?.dry_run ? `Import ${reading.counts.new} new` : 'Import'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
